import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload } from "lucide-react";

const COMPS = [["TRABALHO", "Trab.", 2], ["TESTE", "Teste", 3], ["PROVA", "Prova", 5]];

const StudentPick = ({ value, students, onChange, testid }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className="h-8 w-full rounded-md border border-slate-200 px-1 text-xs" data-testid={testid}>
    <option value="">— não identificado —</option>
    {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
  </select>
);

function Review({ r, setR, students }) {
  const set = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const setRow = (key, i, patch) => setR((x) => ({ ...x, [key]: x[key].map((row, j) => (j === i ? { ...row, ...patch } : row)) }));
  return (
    <div className="space-y-3 text-sm" data-testid="diary-ai-review">
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Data</Label><Input type="date" value={r.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="diary-ai-date" /></div>
        <div><Label>Nº de tempos</Label><Input type="number" min="1" value={r.num_times} onChange={(e) => set("num_times", e.target.value)} data-testid="diary-ai-times" /></div>
      </div>
      <div><Label>Conteúdo</Label><Textarea rows={2} value={r.content} onChange={(e) => set("content", e.target.value)} data-testid="diary-ai-content" /></div>
      <div>
        <Label>Faltas ({r.absent.length}) — os demais ficam presentes</Label>
        {r.absent.map((a, i) => (
          <div key={i} className="mt-1 grid grid-cols-[1fr_1.4fr] items-center gap-2"><span className="truncate text-xs text-slate-500">Lido: {a.name}</span>
            <StudentPick value={a.student_id} students={students} onChange={(v) => setRow("absent", i, { student_id: v })} testid={`diary-ai-absent-${i}`} /></div>
        ))}
      </div>
      {r.grades.length > 0 && (
        <div>
          <Label>Notas</Label>
          <div className="mt-1 grid grid-cols-[1.4fr_repeat(3,56px)] gap-1 text-[11px] font-semibold text-slate-500"><span>Aluno</span>{COMPS.map(([k, label, mx]) => <span key={k} className="text-center">{label} (0-{mx})</span>)}</div>
          {r.grades.map((g, i) => (
            <div key={i} className="mt-1 grid grid-cols-[1.4fr_repeat(3,56px)] items-center gap-1">
              <StudentPick value={g.student_id} students={students} onChange={(v) => setRow("grades", i, { student_id: v })} testid={`diary-ai-grade-student-${i}`} />
              {COMPS.map(([k, label, mx]) => (
                <Input key={k} type="number" step="0.1" min="0" max={mx} placeholder={label} value={g[k] ?? ""} className="h-8 px-1 text-xs"
                  onChange={(e) => setRow("grades", i, { [k]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`diary-ai-grade-${k}-${i}`} />
              ))}
            </div>
          ))}
        </div>
      )}
      {r.illegible?.length > 0 && <p className="rounded bg-amber-50 p-2 text-xs text-amber-700" data-testid="diary-ai-illegible">Ilegível: {r.illegible.join("; ")}</p>}
    </div>
  );
}

export function DiaryAiDialog({ open, setOpen, classId, subjectId, term, students, onDone }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => { setOpen(false); setR(null); };
  const read = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("class_id", classId);
    [...files].forEach((f) => fd.append("files", f));
    try { setR((await api.post("/diary/ai-read", fd, { timeout: 180000 })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/diary/ai-apply", {
        class_id: classId, subject_id: subjectId, term_order: term, date: r.date, num_times: Number(r.num_times) || 1, content: r.content,
        absent_ids: r.absent.map((a) => a.student_id).filter(Boolean),
        grades: r.grades.filter((g) => g.student_id).map(({ student_id, TRABALHO, TESTE, PROVA }) => ({ student_id, TRABALHO, TESTE, PROVA })),
      });
      toast.success(`Diário lançado: ${data.absent} falta(s), ${data.grades} aluno(s) com nota`);
      close(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="diary-ai-dialog">
        <DialogHeader><DialogTitle>Ler foto do diário</DialogTitle></DialogHeader>
        {!r ? (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-500 hover:bg-slate-50">
            {busy ? <Loader2 className="h-6 w-6 animate-spin text-blue-700" /> : <Upload className="h-6 w-6 text-blue-700" />}
            {busy ? "A IA está lendo o diário…" : "Fotografe ou envie a página do diário preenchida à mão"}
            <input type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden" disabled={busy} onChange={(e) => { read(e.target.files); e.target.value = ""; }} data-testid="diary-ai-file" />
          </label>
        ) : <Review r={r} setR={setR} students={students} />}
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="diary-ai-cancel">Cancelar</Button>
          {r && <Button onClick={apply} disabled={busy || !r.date} className="bg-blue-700 hover:bg-blue-800" data-testid="diary-ai-apply">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Confirmar e lançar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
